<template>
  <div class="portal-page">
    <div class="portal-container">
      <!-- Encabezado -->



      <!-- Pestañas -->
      <div class="tabs">
        <button
          v-for="t in tabs"
          :key="t.id"
          class="tab"
          :class="{ on: vista === t.id }"
          @click="vista = t.id"
        >
          <PortalIcon :name="t.icon" :size="16" /> {{ t.label }}
        </button>
      </div>

      <!-- Contenido -->
      <div class="content">
        <div v-if="loading" class="loading">Cargando portal…</div>
        <div v-else-if="error" class="error">{{ error }}</div>
        <template v-else>
          <PanelTab v-if="vista === 'panel'" :reg="reg" @navigate="vista = $event" />
          <RegistrosTab
            v-else-if="vista === 'registros'"
            :reg="reg"
            @update-estatus="updateEstatus"
            @data-updated="casos = $event"
            @import="importar"
          />
          <PlanesTab
            v-else-if="vista === 'planes'"
            :claims="casos"
          />
          <BandejasTab v-else-if="vista === 'bandejas'" />
          <CronogramaTab
            v-else-if="vista === 'gantt'"
            :reg="reg"
            :now="now"
          />
          <AreasTab v-else-if="vista === 'areas'" :reg="reg" />
        </template>
      </div>

      <!-- Pie -->
      <div class="footer">
        <span class="f-total">
          {{ casos.length }} incidencias · datos en vivo (ORDS)
        </span>
        <button class="f-reset" :disabled="loading" @click="reiniciar">
          <PortalIcon name="RotateCcw" :size="13" /> Recargar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PortalIcon from "./portal/PortalIcon.vue";
import PanelTab from "./portal/PanelTab.vue";
import RegistrosTab from "./portal/RegistrosTab.vue";
import PlanesTab from "./portal/PlanesTab.vue";
import BandejasTab from "./portal/BandejasTab.vue";
import CronogramaTab from "./portal/CronogramaTab.vue";
import AreasTab from "./portal/AreasTab.vue";
import portalService from "../../services/portal.service";
import { slaDe, avanceDe } from "./portal/portal.helpers";

export default {
  name: "PortalSeguimiento",
  components: {
    PortalIcon, PanelTab, RegistrosTab, PlanesTab, BandejasTab,
    CronogramaTab, AreasTab,
  },
  data() {
    return {
      casos: [],
      now: new Date(),
      vista: "panel",
      loading: true,
      error: "",
      pasos: ["Entrevista CX", "Área responsable", "Seguimiento", "Cierre"],
      tabs: [
        { id: "panel", label: "Panel", icon: "LayoutDashboard" },
        { id: "registros", label: "Incidencias", icon: "ListChecks" },
        { id: "planes", label: "Planes de acción", icon: "Flag" },
        { id: "bandejas", label: "Bandejas", icon: "CheckCircle2" },
        { id: "gantt", label: "Cronograma", icon: "CalendarRange" },
        { id: "areas", label: "Áreas", icon: "Building2" },
      ],
    };
  },
  computed: {
    // Registros enriquecidos con SLA y % de avance calculados.
    reg() {
      return this.casos.map((c) => ({
        ...c,
        _sla: slaDe(c, this.now),
        _av: avanceDe(c),
      }));
    },
    fechaHoy() {
      return this.now.toLocaleDateString("es-MX", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    },
  },
  async mounted() {
    await this.cargar();
  },
  methods: {
    async cargar() {
      this.loading = true;
      this.error = "";
      try {
        this.casos = await portalService.fetchRegistros();
      } catch (e) {
        this.error = "No se pudieron cargar los registros del portal.";
      } finally {
        this.loading = false;
      }
    },
    async updateEstatus(id, estatus) {
      this.casos = await portalService.updateEstatus(id, estatus);
    },
    async importar(nuevos) {
      const { list } = await portalService.importRegistros(nuevos);
      this.casos = list;
    },
    // Vuelve a leer del servidor (descarta cambios locales optimistas).
    async reiniciar() {
      await this.cargar();
    },
  },
};
</script>

<style scoped>
.portal-page {
  /* Tema local del portal. Claro por defecto; se sobrescribe bajo html.dark.
     Igual al resto de la app (fondo blanco). Los colores semánticos
     (estatus/área/prioridad/marca) NO se tematizan: son significativos. */
  --pt-bg: var(--white, #ffffff);
  --pt-card: #ffffff;
  --pt-elev: #F5F5F5;
  --pt-track: #f0f4fa;
  --pt-line: #f0f4fa;
  --pt-border: #F5F5F5;
  --pt-text: #0f172a;
  --pt-dim: #64748b;
  --pt-muted: #94a3b8;

  background: var(--pt-bg);
  min-height: calc(100vh - 64px);
  color: var(--pt-text);
  box-sizing: border-box;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto,
    Helvetica, Arial, sans-serif;
}

/* Modo oscuro: se activa con la clase .dark en <html> (mismo toggle del navbar).
   Las variables se heredan a todos los componentes hijos del portal. */
html.dark .portal-page {
  --pt-bg: #0b1120;
  --pt-card: #141d30;
  --pt-elev: #1e2a44;
  --pt-track: #1e2a44;
  --pt-line: rgba(255, 255, 255, 0.08);
  --pt-border: rgba(255, 255, 255, 0.1);
  --pt-text: #e6edf7;
  --pt-dim: #9fb0c9;
  --pt-muted: #7686a3;
}
.portal-container {
  max-width: 1400px; /* mismo ancho que DashboardGeneral (.dg) */
  margin: 0 auto;
  padding: clamp(16px, 3vw, 32px);
}

/* Encabezado */
.p-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.eyebrow {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #003ca5;
}
.p-title {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 4px 0 0;
  color: var(--pt-text);
}
.p-date {
  text-align: right;
  font-size: 12px;
  color: var(--pt-dim);
  font-variant-numeric: tabular-nums;
}

/* Flujo */
.flow {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  font-size: 12px;
  font-weight: 500;
  color: var(--pt-dim);
}
.flow-step {
  display: flex;
  align-items: center;
  gap: 4px;
}
.flow-pill {
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--pt-card);
  border: 1px solid var(--pt-border);
}
.flow-chevron {
  color: var(--pt-muted);
}

/* Pestañas */
.tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  overflow-x: auto;
  background: var(--pt-elev);
}
.tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  border: 0;
  cursor: pointer;
  background: transparent;
  color: var(--pt-dim);
}
.tab.on {
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  color: var(--on-accent);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

/* Contenido */
.content {
  margin-top: 20px;
}
.loading,
.error {
  text-align: center;
  font-size: 14px;
  padding: 48px 0;
}
.loading {
  color: var(--pt-muted);
}
.error {
  color: #b91c1c;
}

/* Pie */
.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--pt-border);
}
.f-total {
  font-size: 12px;
  color: var(--pt-muted);
}
.f-reset {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--pt-dim);
  background: none;
  border: 0;
  cursor: pointer;
}

@media (max-width: 640px) {
  .p-title {
    font-size: 22px;
  }
}
</style>
